import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { Confetti as ConfettiIcon, ShareNetwork, X, Trophy } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const DISMISS_KEY = 'breth_recap_dismissed_week';

/**
 * Sunday-only "week wrap" card that appears on Home showing the user's
 * previous-week volume + PBs. Dismisses on tap and remembers the week
 * so we don't nag them all Sunday.
 */
export default function WeeklyRecapCard() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API_URL}/progress/weekly-recap`, { withCredentials: true })
      .then(({ data }) => {
        if (!alive) return;
        // Only show on Sunday, and only if the user actually trained
        if (!data?.is_sunday) return;
        if (!data?.workouts || data.workouts === 0) return;
        const dismissed = localStorage.getItem(DISMISS_KEY);
        if (dismissed === data.week_start) return;
        setData(data);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const dismiss = () => {
    if (data?.week_start) localStorage.setItem(DISMISS_KEY, data.week_start);
    setData(null);
  };

  const share = async () => {
    if (!data) return;
    const text =
      `📊 My BRETH week\n` +
      `${data.workouts} workouts · ${data.total_sets} sets\n` +
      `${Math.round(data.total_volume).toLocaleString()}kg lifted\n` +
      `${data.personal_bests} new PBs 🏋️`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'My BRETH week', text });
      } else {
        await navigator.clipboard.writeText(text);
        alert('Recap copied — paste it wherever you want to share!');
      }
    } catch (e) {
      // navigator.share throws AbortError when the user cancels the share sheet
      // — that's an expected UX outcome, not a bug. Only log real failures.
      if (e && e.name !== 'AbortError') console.warn('Share failed:', e);
    }
  };

  if (!data) return null;

  return (
    <div
      className="rounded-ios-lg p-5 mb-6 animate-pop-in shadow-ios overflow-hidden relative"
      style={{ background: 'linear-gradient(135deg,#191C21 0%, #2A1D1E 60%, #C0392F 130%)', color: '#EDEAE3' }}
      data-testid="weekly-recap-card"
    >
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 w-8 h-8 rounded-ios-pill bg-bone/10 flex items-center justify-center hover:bg-bone/25"
        aria-label="Dismiss weekly recap"
        data-testid="dismiss-recap-button"
      >
        <X size={14} weight="bold" />
      </button>

      <div className="flex items-center gap-2 mb-1">
        <ConfettiIcon size={16} weight="fill" className="text-bone/80" />
        <p className="text-[10px] uppercase tracking-[0.24em] text-bone/70 font-bold">
          Week Recap
        </p>
      </div>
      <p className="text-[10px] uppercase tracking-[0.14em] text-bone/50 font-semibold mb-3">
        {format(parseISO(data.week_start), 'MMM d')} — {format(parseISO(data.week_end), 'MMM d')}
      </p>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <RecapStat label="Workouts" value={data.workouts} testId="recap-workouts" />
        <RecapStat label="Sets" value={data.total_sets} testId="recap-sets" />
        <RecapStat
          label="Reps"
          value={data.total_reps.toLocaleString()}
          testId="recap-reps"
        />
        <RecapStat
          label="Volume"
          value={Math.round(data.total_volume).toLocaleString()}
          suffix="kg"
          testId="recap-volume"
        />
      </div>

      {data.personal_bests > 0 && (
        <div className="flex items-center gap-2 rounded-ios-pill bg-bone/10 px-3 py-2 mb-3">
          <Trophy size={14} weight="fill" className="text-bone" />
          <p className="text-xs uppercase tracking-[0.18em] font-black text-bone">
            {data.personal_bests} New PB{data.personal_bests === 1 ? '' : 's'}
          </p>
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={share}
          className="flex-1 h-11 bg-bone text-graphite font-black text-xs uppercase tracking-[0.18em] rounded-ios-pill flex items-center justify-center gap-2"
          data-testid="share-recap-button"
        >
          <ShareNetwork size={14} weight="bold" />
          Share
        </button>
        <button
          onClick={dismiss}
          className="h-11 px-5 border border-bone/30 text-bone font-bold text-xs uppercase tracking-[0.15em] rounded-ios-pill hover:bg-bone/10"
          data-testid="close-recap-button"
        >
          Close
        </button>
      </div>
    </div>
  );
}

function RecapStat({ label, value, suffix, testId }) {
  return (
    <div className="rounded-ios px-3 py-2 bg-bone/10" data-testid={testId}>
      <p className="text-[9px] uppercase tracking-[0.22em] font-bold text-bone/55 leading-none">
        {label}
      </p>
      <p className="text-3xl font-black font-heading tabular-nums text-bone leading-tight mt-1">
        {value}
        {suffix && <span className="text-sm font-normal text-bone/70 ml-0.5">{suffix}</span>}
      </p>
    </div>
  );
}
